<template>
  <div>
    <authorisation-wrapper
      :resource="ACCOUNTS"
      :action="WRITE"
      v-slot="{ isAllowed: isWriteAllowed }"
    >
      <cl-button
        :variant="'danger'"
        type="button"
        :readonly="!isWriteAllowed"
        :data-test-id="`${dataTestId}-delete-button`"
        @on-click="modalVisible = true"
      >
        {{ t("delete") }} {{ `(${mspsToDelete.length})` }}
      </cl-button>
    </authorisation-wrapper>

    <t-delete-confirm
      :visible="modalVisible"
      :loading="loading"
      @on-close="modalVisible = false"
      @on-confirm="confirmDelete"
    >
      <template #title>
        <cl-heading :size="'h4'">
          {{ t("confirm-delete") }}
        </cl-heading>
      </template>
      <template #body>
        <span v-if="mspsToDelete.length === 1">
          {{ `${t("delete-single-msp-prompt")}:` }}
          <b>{{ mspsToDelete[0].name }}</b>
        </span>
        <span v-else>
          {{
            `${t("delete-multiple-msps-prompt", { amount: mspsToDelete.length })}`
          }}
        </span>
      </template>
    </t-delete-confirm>
  </div>
</template>

<script setup lang="ts">
import TDeleteConfirm from "@/modals/TDeleteConfirm.vue";
import { ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { useOrganisationsStore } from "@/store/organisations.store";
import { Msp } from "@/views/System/Overview/MspTable/types";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { ACCOUNTS, AuthorisationWrapper, WRITE } from "@/authorisation";
import { isAxiosError } from "axios";

interface Props {
  dataTestId: string;
  mspsToDelete: Msp[];
}

const toastStore = useToastStore();
const organisationsStore = useOrganisationsStore();
const { t } = useI18n();
const DELETE_DEENDANT_ORGANISATIONS_ERROR_MESSAGE =
  "All dependent organisations need to be deleted first";

const props = defineProps<Props>();
const { dataTestId, mspsToDelete } = toRefs(props);

const modalVisible = ref<boolean>(false);
const loading = ref<boolean>(false);

const deletedArray = ref<string[]>([]);
const confirmDelete = async () => {
  loading.value = true;
  try {
    const response = await organisationsStore.batchDeleteOrganisations({
      accounts: mspsToDelete.value,
    });
    deletedArray.value.push(...response);
  } catch (e: unknown) {
    loading.value = false;
    if (isAxiosError(e)) {
      toastStore.displayToast({
        title: t("error"),
        message:
          e.response?.status === 403 &&
          e.response?.data.error === DELETE_DEENDANT_ORGANISATIONS_ERROR_MESSAGE
            ? t("error-msp-customers-assigned")
            : t("subtext-sys-error-default"),
        duration: 5000,
        variant: "danger",
        testId: "msp-delete-toast-error",
      });
    }
  } finally {
    loading.value = false;
    if (deletedArray.value.length > 0) {
      toastStore.displayToast({
        title: t("success"),
        message:
          deletedArray.value.length > 1
            ? t("msp-deleted", {
                amount: deletedArray.value.length,
              })
            : t("msp-deleted-successfully"),
        duration: 5000,
        variant: "success",
        testId: "msp-delete-toast-success",
      });
      modalVisible.value = false;
    }
  }
};
</script>
